import { useEffect, useState } from 'react';
import { comidaEmpleadosService, inventarioService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { descargarBlob } from '../utils/descargar';
import { Download, Plus, Trash2, Sandwich, Check, X } from 'lucide-react';
import { io } from 'socket.io-client';
import { SOCKET_URL } from '../utils/socket';

function fmt(n: any) {
  return parseFloat(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const TIPOS = [
  ['desayuno', 'Desayuno'],
  ['comida', 'Comida'],
  ['cena', 'Cena'],
  ['otro', 'Otro'],
] as const;

const card: React.CSSProperties = { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.06)' };
const labelStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 6 };
const inputStyle: React.CSSProperties = { width: '100%', height: 38, border: '1px solid var(--line-strong)', borderRadius: 8, padding: '0 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
const th: React.CSSProperties = { padding: '10px 16px', fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', textAlign: 'left' };
const td: React.CSSProperties = { padding: '10px 16px', fontSize: 13, color: 'var(--ink-2)', borderTop: '1px solid var(--line-2)' };

type Renglon = {
  origen: 'producto' | 'insumo';
  id: string;
  descripcion: string;
  unidad: string;
  cantidad: number;
  costo: number;      // estimado en el panel; el backend recalcula al guardar
  sin_receta?: boolean;
};

export default function ComidaEmpleados() {
  const hoy = new Date().toISOString().slice(0, 10);
  const { usuario } = useAuth();
  const esAdmin = usuario?.rol === 'admin';

  const [tab, setTab] = useState<'pendientes' | 'registrar' | 'historial' | 'reporte'>('registrar');
  const [pendientes, setPendientes] = useState<any[]>([]);

  const [empleados, setEmpleados] = useState<any[]>([]);
  const [insumos, setInsumos] = useState<any[]>([]);
  const [productos, setProductos] = useState<any[]>([]);   // de /inventario/costos: ya trae costo_receta

  // Formulario
  const [empleadoId, setEmpleadoId] = useState('');
  const [tipoComida, setTipoComida] = useState<string>('comida');
  const [nota, setNota] = useState('');
  const [renglones, setRenglones] = useState<Renglon[]>([]);
  const [origen, setOrigen] = useState<'producto' | 'insumo'>('producto');
  const [seleccion, setSeleccion] = useState('');
  const [cantidad, setCantidad] = useState('1');

  const [guardando, setGuardando] = useState(false);
  const [ok, setOk] = useState('');
  const [avisos, setAvisos] = useState<string[]>([]);
  const [error, setError] = useState('');

  const [historial, setHistorial] = useState<any[]>([]);

  const [fi, setFi] = useState(hoy);
  const [ff, setFf] = useState(hoy);
  const [resumen, setResumen] = useState<any>(null);
  const [cargandoReporte, setCargandoReporte] = useState(false);

  useEffect(() => {
    cargar();
    // Lo que se pide desde los teléfonos aparece aquí sin recargar
    const socket = io(SOCKET_URL, { auth: { token: localStorage.getItem('token') } });
    socket.on('comida_empleados_actualizada', () => cargar());
    return () => { socket.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Autorizar pide la contraseña de alguien con el permiso "Autorizar comida
     de empleados" (Usuarios). Si el empleado ya llegó a su límite del día,
     el backend responde LIMITE y se pregunta si se aprueba como extra. */
  const [pideClave, setPideClave] = useState<null | { titulo: string; resolve: (c: string | null) => void }>(null);
  const [clave, setClave] = useState('');
  const pedirClave = (titulo: string) =>
    new Promise<string | null>(resolve => { setClave(''); setPideClave({ titulo, resolve }); });
  const cerrarClave = (v: string | null) => { pideClave?.resolve(v); setPideClave(null); setClave(''); };

  const conAutorizacion = async (titulo: string, accion: (clave: string, extra: boolean) => Promise<any>) => {
    const c = await pedirClave(titulo);
    if (!c) return null;
    try { return await accion(c, false); }
    catch (e: any) {
      if (e.response?.data?.codigo !== 'LIMITE') throw e;
      if (!window.confirm(`${e.response.data.error}\n\n¿Aprobarla de todos modos como comida extra?`)) return null;
      return accion(c, true);
    }
  };

  const aprobar = async (p: any) => {
    setError(''); setOk(''); setAvisos([]);
    try {
      const r = await conAutorizacion(`Aprobar la comida de ${p.empleado}`, (c, extra) => comidaEmpleadosService.aprobar(p.id, c, extra));
      if (!r) return;
      setOk(`Aprobada por ${r.data.autorizado_por} — $${fmt(r.data.costo_total)}`);
      setAvisos(r.data.advertencias || []);
      cargar();
    } catch (e: any) { setError(e.response?.data?.error || 'No se pudo aprobar'); }
  };
  const rechazar = async (p: any) => {
    setError(''); setOk('');
    try {
      const c = await pedirClave(`Rechazar la comida de ${p.empleado}`);
      if (!c) return;
      await comidaEmpleadosService.rechazar(p.id, c);
      setOk(`Solicitud de ${p.empleado} rechazada`);
      cargar();
    } catch (e: any) { setError(e.response?.data?.error || 'No se pudo rechazar'); }
  };

  const cargar = async () => {
    try {
      const [emp, ins, cos, hist, pend] = await Promise.all([
        comidaEmpleadosService.getEmpleados(),
        inventarioService.getInsumos(),
        inventarioService.getCostos(),
        comidaEmpleadosService.getAll({ limite: 30 }),
        comidaEmpleadosService.getPendientes(),
      ]);
      setPendientes(pend.data);
      setEmpleados(emp.data);
      setInsumos(ins.data);
      setProductos(cos.data);
      setHistorial(hist.data);
    } catch (e: any) { setError(e.response?.data?.error || 'No se pudieron cargar los datos'); }
  };

  const agregarRenglon = () => {
    setError('');
    const cant = parseFloat(cantidad);
    if (!seleccion) { setError('Elige un producto o un insumo'); return; }
    if (!isFinite(cant) || cant <= 0) { setError('La cantidad debe ser mayor a 0'); return; }

    if (origen === 'producto') {
      const p = productos.find(x => x.id === seleccion);
      if (!p) return;
      const costoReceta = parseFloat(p.costo_receta || 0);
      setRenglones(r => [...r, {
        origen: 'producto', id: p.id, descripcion: p.nombre, unidad: 'porción',
        cantidad: cant, costo: costoReceta * cant, sin_receta: costoReceta === 0,
      }]);
    } else {
      const i = insumos.find(x => x.id === seleccion);
      if (!i) return;
      setRenglones(r => [...r, {
        origen: 'insumo', id: i.id, descripcion: i.nombre, unidad: i.unidad,
        cantidad: cant, costo: parseFloat(i.costo_unitario || 0) * cant,
      }]);
    }
    setSeleccion(''); setCantidad('1');
  };

  const quitarRenglon = (idx: number) => setRenglones(r => r.filter((_, i) => i !== idx));

  const totalEstimado = renglones.reduce((a, r) => a + r.costo, 0);

  const registrar = async (aprobarAhora: boolean) => {
    setError(''); setOk(''); setAvisos([]);
    if (!empleadoId) { setError('Selecciona al empleado'); return; }
    if (renglones.length === 0) { setError('Agrega al menos un producto o insumo'); return; }

    setGuardando(true);
    try {
      const datos = {
        empleado_id: empleadoId,
        tipo_comida: tipoComida,
        nota: nota.trim() || undefined,
        items: renglones.map(r => r.origen === 'producto'
          ? { producto_id: r.id, cantidad: r.cantidad }
          : { insumo_id: r.id, cantidad: r.cantidad }),
      };
      const nombre = empleados.find(e => e.id === empleadoId)?.nombre || 'el empleado';
      const res = aprobarAhora
        ? await conAutorizacion(`Aprobar la comida de ${nombre}`, (c, extra) => comidaEmpleadosService.registrar({ ...datos, password: c, extra }))
        : await comidaEmpleadosService.registrar(datos);
      if (!res) return;
      setOk(res.data.estado === 'aprobado'
        ? `Aprobada por ${res.data.autorizado_por}: ${res.data.empleado} — $${fmt(res.data.costo_total)} (${res.data.insumos_descontados} insumo(s) descontado(s))`
        : `Solicitud guardada como pendiente: ${res.data.empleado}. Se descuenta del inventario cuando alguien autorizado la apruebe.`);
      setAvisos(res.data.advertencias || []);
      setRenglones([]); setNota('');
      cargar();
      setTimeout(() => setOk(''), 6000);
    } catch (e: any) { setError(e.response?.data?.error || 'Error al registrar'); }
    finally { setGuardando(false); }
  };

  const cancelar = async (id: string) => {
    if (!window.confirm('¿Cancelar este consumo y devolver los insumos al inventario?')) return;
    setError('');
    try {
      await comidaEmpleadosService.cancelar(id);
      cargar();
    } catch (e: any) { setError(e.response?.data?.error || 'Error al cancelar'); }
  };

  const consultarReporte = async () => {
    setCargandoReporte(true); setError('');
    try { const res = await comidaEmpleadosService.getResumen(fi, ff); setResumen(res.data); }
    catch (e: any) { setError(e.response?.data?.error || 'Error al consultar'); }
    finally { setCargandoReporte(false); }
  };

  const exportarCSV = () => {
    if (!resumen) return;
    const filas = [
      ['Empleado', 'Rol', 'Consumos', 'Costo'],
      ...resumen.por_empleado.map((r: any) => [r.empleado, r.rol, r.num_consumos, fmt(r.costo)]),
      [],
      ['Total', '', resumen.totales.num_consumos, fmt(resumen.totales.costo)],
      [],
      ['Insumo', 'Unidad', 'Cantidad', 'Costo'],
      ...resumen.por_insumo.map((r: any) => [r.insumo, r.unidad, fmt(r.cantidad), fmt(r.costo)]),
    ];
    const csv = filas.map(r => r.join(',')).join('\n');
    descargarBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `comida-empleados-${fi}-${ff}.csv`);
  };

  return (
    <div style={{ maxWidth: 860 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.02em', margin: 0 }}>Comida de empleados</h1>
        <p style={{ fontSize: 13, color: 'var(--muted)', margin: '2px 0 0' }}>
          Consumo interno del personal. Se pide aquí o desde el teléfono y se descuenta del inventario hasta que alguien autorizado lo aprueba con su contraseña
        </p>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'var(--well)', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {([['pendientes', `Por autorizar${pendientes.length ? ` (${pendientes.length})` : ''}`], ['registrar', 'Registrar'], ['historial', 'Historial'], ['reporte', 'Reporte']] as const).map(([t, label]) => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', background: tab === t ? 'var(--surface)' : 'transparent', color: tab === t ? 'var(--brand-texto)' : 'var(--muted)', boxShadow: tab === t ? '0 1px 2px rgba(0,0,0,.08)' : 'none' }}>
            {label}
          </button>
        ))}
      </div>

      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--brand-texto)', fontSize: 13, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--danger-line)', marginBottom: 14 }}>{error}</div>}
      {tab !== 'registrar' && ok && <div style={{ background: 'var(--ok-bg)', color: 'var(--ok)', fontSize: 13, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--ok-line)', marginBottom: 14 }}>{ok}</div>}

      {tab === 'pendientes' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {pendientes.length === 0 && (
            <div style={{ ...card, padding: 20, color: 'var(--faint)', fontSize: 13 }}>No hay comidas por autorizar</div>
          )}
          {pendientes.map(p => (
            <div key={p.id} style={{ ...card, padding: 16, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{p.empleado}
                  <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--faint)', marginLeft: 8, textTransform: 'capitalize' }}>{p.tipo_comida}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                  {(p.items || []).map((i: any, k: number) => <div key={k}>{parseFloat(i.cantidad)} × {i.descripcion}</div>)}
                  {p.nota && <div style={{ color: 'var(--faint)', marginTop: 2 }}>“{p.nota}”</div>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 4 }}>
                  Pidió {p.registrado_por_nombre} · {new Date(p.created_at).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => rechazar(p)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, height: 36, padding: '0 14px', border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: 'var(--muted)' }}>
                  <X size={14} /> Rechazar
                </button>
                <button onClick={() => aprobar(p)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, height: 36, padding: '0 16px', border: 'none', background: 'var(--ok)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#fff' }}>
                  <Check size={14} /> Aprobar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'registrar' && (
        <div style={{ ...card, padding: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sandwich size={16} /> Nuevo consumo
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Empleado *</label>
              <select value={empleadoId} onChange={e => setEmpleadoId(e.target.value)} style={{ ...inputStyle, background: 'var(--surface)', cursor: 'pointer' }}>
                <option value="">Seleccionar empleado</option>
                {empleados.map(e => <option key={e.id} value={e.id}>{e.nombre} — {e.rol}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Tipo de comida</label>
              <select value={tipoComida} onChange={e => setTipoComida(e.target.value)} style={{ ...inputStyle, background: 'var(--surface)', cursor: 'pointer' }}>
                {TIPOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>

          {/* Armador de renglones: del menú (descuenta por receta) o insumo suelto */}
          <div style={{ borderTop: '1px solid var(--line-2)', paddingTop: 14 }}>
            <div style={{ display: 'flex', gap: 4, marginBottom: 12, background: 'var(--well)', borderRadius: 8, padding: 3, width: 'fit-content' }}>
              {([['producto', 'Del menú'], ['insumo', 'Insumo suelto']] as const).map(([o, l]) => (
                <button key={o} onClick={() => { setOrigen(o); setSeleccion(''); }}
                  style={{ padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', background: origen === o ? 'var(--surface)' : 'transparent', color: origen === o ? 'var(--brand-texto)' : 'var(--muted)' }}>
                  {l}
                </button>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr auto', gap: 10, alignItems: 'end' }}>
              <div>
                <label style={labelStyle}>{origen === 'producto' ? 'Producto del menú' : 'Insumo'}</label>
                <select value={seleccion} onChange={e => setSeleccion(e.target.value)} style={{ ...inputStyle, background: 'var(--surface)', cursor: 'pointer' }}>
                  <option value="">Seleccionar</option>
                  {origen === 'producto'
                    ? productos.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.nombre}{parseFloat(p.costo_receta || 0) === 0 ? ' (sin receta)' : ` — costo $${fmt(p.costo_receta)}`}
                        </option>
                      ))
                    : insumos.map(i => (
                        <option key={i.id} value={i.id}>
                          {i.nombre} — Stock: {parseFloat(i.stock_actual).toFixed(2)} {i.unidad}
                        </option>
                      ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Cantidad</label>
                <input type="number" min="0" step="0.001" value={cantidad} onChange={e => setCantidad(e.target.value)} style={inputStyle} />
              </div>
              <button onClick={agregarRenglon}
                style={{ display: 'flex', alignItems: 'center', gap: 6, height: 38, padding: '0 14px', border: '1px solid var(--line-strong)', background: 'var(--surface)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: 'var(--ink-2)' }}>
                <Plus size={14} /> Agregar
              </button>
            </div>
          </div>

          {renglones.length > 0 && (
            <div style={{ marginTop: 16, border: '1px solid var(--line-2)', borderRadius: 10, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: 'var(--bg)' }}>
                  <tr>
                    <th style={th}>Concepto</th>
                    <th style={{ ...th, textAlign: 'center' }}>Cantidad</th>
                    <th style={{ ...th, textAlign: 'right' }}>Costo estimado</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {renglones.map((r, idx) => (
                    <tr key={idx}>
                      <td style={td}>
                        {r.descripcion}
                        <span style={{ fontSize: 11, color: 'var(--faint)', marginLeft: 6 }}>
                          {r.origen === 'producto' ? 'menú' : 'insumo'}
                        </span>
                        {r.sin_receta && (
                          <div style={{ fontSize: 11, color: 'var(--warn)' }}>Sin receta: no descontará inventario</div>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: 'center', color: 'var(--muted)' }}>{r.cantidad} {r.unidad}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>${fmt(r.costo)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <button onClick={() => quitarRenglon(idx)} title="Quitar"
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--faint)' }}>
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: 'var(--bg)' }}>
                    <td style={{ ...td, fontWeight: 700 }} colSpan={2}>Costo estimado total</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: 'var(--brand-texto)' }}>${fmt(totalEstimado)}</td>
                    <td style={td}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <div style={{ marginTop: 14 }}>
            <label style={labelStyle}>Nota (opcional)</label>
            <input value={nota} onChange={e => setNota(e.target.value)} placeholder="Ej. Comida de turno, cortesía por doble turno" style={inputStyle} />
          </div>

          {ok && <div style={{ background: 'var(--ok-bg)', color: 'var(--ok)', fontSize: 13, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--ok-line)', marginTop: 14 }}>{ok}</div>}
          {avisos.length > 0 && (
            <div style={{ background: 'var(--warn-bg)', color: 'var(--warn)', fontSize: 12, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--warn-line)', marginTop: 10 }}>
              {avisos.map((a, i) => <div key={i}>⚠ {a}</div>)}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button onClick={() => registrar(false)} disabled={guardando}
              style={{ flex: 1, height: 40, border: '1px solid var(--line-strong)', borderRadius: 8, background: 'var(--surface)', color: 'var(--ink-2)', fontSize: 13, fontWeight: 600, cursor: guardando ? 'not-allowed' : 'pointer', opacity: guardando ? .7 : 1 }}>
              Guardar como pendiente
            </button>
            <button onClick={() => registrar(true)} disabled={guardando}
              style={{ flex: 1, height: 40, border: 'none', borderRadius: 8, background: 'var(--brand)', color: 'var(--on-brand)', fontSize: 13, fontWeight: 600, cursor: guardando ? 'not-allowed' : 'pointer', opacity: guardando ? .7 : 1 }}>
              {guardando ? 'Guardando…' : 'Aprobar ahora (contraseña)'}
            </button>
          </div>
        </div>
      )}

      {tab === 'historial' && (
        <div style={{ ...card, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: 'var(--bg)' }}>
              <tr>
                <th style={th}>Empleado</th>
                <th style={th}>Consumo</th>
                <th style={{ ...th, textAlign: 'right' }}>Costo</th>
                <th style={{ ...th, textAlign: 'right' }}>Fecha</th>
                {esAdmin && <th style={th}></th>}
              </tr>
            </thead>
            <tbody>
              {historial.length === 0 && (
                <tr><td style={{ ...td, color: 'var(--faint)' }} colSpan={esAdmin ? 5 : 4}>Todavía no hay consumos registrados</td></tr>
              )}
              {historial.map(h => (
                <tr key={h.id} style={{ opacity: h.cancelado || h.estado === 'rechazado' ? .5 : 1 }}>
                  <td style={td}>
                    <div style={{ fontWeight: 500 }}>{h.empleado}</div>
                    <div style={{ fontSize: 11, color: 'var(--faint)', textTransform: 'capitalize' }}>
                      {h.tipo_comida} · pidió {h.registrado_por_nombre}
                      {h.estado === 'aprobado' && h.autorizado_por_nombre ? ` · autorizó ${h.autorizado_por_nombre}` : ''}
                      {h.estado === 'pendiente' ? ' · POR AUTORIZAR' : h.estado === 'rechazado' ? ` · RECHAZADA por ${h.autorizado_por_nombre || '—'}` : ''}
                      {h.cancelado ? ' · CANCELADO' : ''}
                    </div>
                  </td>
                  <td style={{ ...td, fontSize: 12, color: 'var(--muted)' }}>
                    {(h.items || []).map((i: any, k: number) => (
                      <div key={k}>{parseFloat(i.cantidad)} × {i.descripcion}</div>
                    ))}
                    {h.nota && <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 2 }}>“{h.nota}”</div>}
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: h.cancelado ? 'var(--faint)' : 'var(--brand-texto)', textDecoration: h.cancelado ? 'line-through' : 'none' }}>
                    ${fmt(h.costo_total)}
                  </td>
                  <td style={{ ...td, textAlign: 'right', color: 'var(--faint)', fontSize: 12 }}>
                    {new Date(h.created_at).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  {esAdmin && (
                    <td style={{ ...td, textAlign: 'right' }}>
                      {!h.cancelado && h.estado === 'aprobado' && (
                        <button onClick={() => cancelar(h.id)}
                          style={{ border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 6, fontSize: 12, padding: '4px 10px', cursor: 'pointer', color: 'var(--muted)' }}>
                          Cancelar
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'reporte' && (
        <div>
          <div style={{ ...card, padding: '14px 16px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {[{ label: 'Desde', value: fi, set: setFi, max: ff }, { label: 'Hasta', value: ff, set: setFf, max: hoy }].map(({ label, value, set, max }) => (
              <div key={label}>
                <label style={labelStyle}>{label}</label>
                <input type="date" value={value} max={max} onChange={e => set(e.target.value)} style={{ ...inputStyle, height: 36 }} />
              </div>
            ))}
            <button onClick={consultarReporte} disabled={cargandoReporte}
              style={{ height: 36, padding: '0 16px', background: 'var(--brand)', color: 'var(--on-brand)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {cargandoReporte ? 'Consultando…' : 'Consultar'}
            </button>
          </div>

          {resumen && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
                <div style={{ ...card, padding: 16 }}>
                  <p style={{ fontSize: 12, color: 'var(--faint)', margin: 0 }}>Costo de comida del personal</p>
                  <p style={{ fontSize: 24, fontWeight: 700, color: 'var(--brand-texto)', margin: '4px 0 0' }}>${fmt(resumen.totales.costo)}</p>
                </div>
                <div style={{ ...card, padding: 16 }}>
                  <p style={{ fontSize: 12, color: 'var(--faint)', margin: 0 }}>Consumos registrados</p>
                  <p style={{ fontSize: 24, fontWeight: 700, margin: '4px 0 0' }}>{resumen.totales.num_consumos}</p>
                </div>
              </div>

              <div style={{ ...card, overflow: 'hidden', marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 14, padding: '16px 16px 0' }}>Por empleado</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                  <thead style={{ background: 'var(--bg)' }}>
                    <tr>
                      <th style={th}>Empleado</th>
                      <th style={{ ...th, textAlign: 'center' }}>Consumos</th>
                      <th style={{ ...th, textAlign: 'right' }}>Costo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resumen.por_empleado.length === 0 && (
                      <tr><td style={{ ...td, color: 'var(--faint)' }} colSpan={3}>Sin consumos en el período</td></tr>
                    )}
                    {resumen.por_empleado.map((r: any) => (
                      <tr key={r.empleado}>
                        <td style={{ ...td, fontWeight: 500 }}>{r.empleado} <span style={{ fontSize: 11, color: 'var(--faint)' }}>{r.rol}</span></td>
                        <td style={{ ...td, textAlign: 'center', color: 'var(--muted)' }}>{r.num_consumos}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: 'var(--brand-texto)' }}>${fmt(r.costo)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ ...card, overflow: 'hidden', marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 14, padding: '16px 16px 0' }}>Insumos más consumidos por el personal</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                  <thead style={{ background: 'var(--bg)' }}>
                    <tr>
                      <th style={th}>Insumo</th>
                      <th style={{ ...th, textAlign: 'center' }}>Cantidad</th>
                      <th style={{ ...th, textAlign: 'right' }}>Costo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resumen.por_insumo.length === 0 && (
                      <tr><td style={{ ...td, color: 'var(--faint)' }} colSpan={3}>Sin descuentos en el período</td></tr>
                    )}
                    {resumen.por_insumo.map((r: any) => (
                      <tr key={r.insumo}>
                        <td style={{ ...td, fontWeight: 500 }}>{r.insumo}</td>
                        <td style={{ ...td, textAlign: 'center', color: 'var(--muted)' }}>{fmt(r.cantidad)} {r.unidad}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>${fmt(r.costo)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button onClick={exportarCSV}
                style={{ display: 'flex', alignItems: 'center', gap: 6, height: 38, padding: '0 16px', border: '1px solid var(--ok)', color: 'var(--ok)', background: 'var(--surface)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                <Download size={14} /> Exportar CSV
              </button>
            </>
          )}
        </div>
      )}

      {pideClave && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => cerrarClave(null)}>
          <form onClick={e => e.stopPropagation()}
            onSubmit={e => { e.preventDefault(); if (clave) cerrarClave(clave); }}
            style={{ background: 'var(--surface)', borderRadius: 12, width: '100%', maxWidth: 380, padding: 20, boxShadow: '0 20px 50px rgba(0,0,0,.25)' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>{pideClave.titulo}</h3>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--muted)' }}>Contraseña de alguien autorizado para aprobar comida de empleados.</p>
            <input type="password" autoFocus autoComplete="off" value={clave} onChange={e => setClave(e.target.value)}
              placeholder="Contraseña" style={{ ...inputStyle, height: 40, fontSize: 14 }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button type="button" onClick={() => cerrarClave(null)} style={{ flex: 1, height: 38, border: '1px solid var(--line-strong)', borderRadius: 8, background: 'var(--surface)', fontSize: 13, cursor: 'pointer', color: 'var(--ink-2)' }}>Cancelar</button>
              <button type="submit" disabled={!clave} style={{ flex: 1, height: 38, border: 'none', borderRadius: 8, background: 'var(--brand)', color: 'var(--on-brand)', fontSize: 13, fontWeight: 600, cursor: clave ? 'pointer' : 'not-allowed', opacity: clave ? 1 : .6 }}>Autorizar</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
